import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Map, NotebookPen, Settings2, KeyRound, ShieldCheck, Footprints, BookMarked, Trash2, RefreshCw, Download, Waypoints } from "lucide-react";
import { ZONES, zoneIndexForPage } from "../lib/reader";
import { TrailProfile, ContourSwatch } from "./TrailProfile";

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="border-t border-ivory-400/50 py-5" data-testid={testId}>
    <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
      <Icon strokeWidth={1.5} className="h-4 w-4 text-gold-deep" />
      {title}
    </h3>
    {children}
  </section>
);

export const YourTrail = ({ open, onClose, reader, config, currentPage, onJump, flags = {}, sessionPrefill = false, onSessionPrefillUsed }) => {
  const state = reader.state || {};
  const settings = state.settings || {};
  const [codeShown, setCodeShown] = useState(null);
  const [draft, setDraft] = useState("");
  const [forSession, setForSession] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const here = zoneIndexForPage(currentPage);
  const currentZone = here >= 0 ? ZONES[here] : null;

  React.useEffect(() => {
    if (open && sessionPrefill) { setForSession(true); onSessionPrefillUsed?.(); }
  }, [open, sessionPrefill, onSessionPrefillUsed]);

  const setSetting = (k, v) =>
    reader.patchState((p) => ({ ...p, settings: { ...(p.settings || {}), [k]: v } }));

  const chooseCheckin = (id) =>
    reader.patchState((p) => ({
      ...p,
      checkin: id,
      trail_log: [...(p.trail_log || []), { type: "checkin", id, at: new Date().toISOString() }],
    }));

  const saveEntry = async () => {
    if (!draft.trim()) return;
    await reader.addJournal({ zone_id: currentZone?.id || state.zone_id, zone_name: currentZone?.name || "Trailhead", body: draft.trim(), for_session: forSession });
    reader.patchState((p) => ({ ...p, trail_log: [...(p.trail_log || []), { type: "journal", zone: currentZone?.name, at: new Date().toISOString() }] }));
    setDraft("");
    setForSession(false);
  };

  const checkinStates = config?.checkin_states || [];
  const activeCheckin = checkinStates.find((c) => c.id === state.checkin);
  const notes = state.field_notes || [];
  const log = [...(state.trail_log || [])].reverse();

  const Chip = ({ active, children, ...rest }) => (
    <button
      type="button"
      {...rest}
      className={`rounded-full border px-3 py-1.5 text-sm transition ${active ? "border-gold bg-gold/15 text-gold-deep" : "border-ivory-400/70 text-ink-soft hover:border-gold/50"}`}
    >
      {children}
    </button>
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] bg-ink/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="your-trail-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[56] flex h-full w-[min(94vw,440px)] flex-col bg-ivory-100 shadow-2xl"
            data-testid="your-trail-panel"
          >
            <div className="flex items-center justify-between px-6 pt-6">
              <div>
                <p className="kicker text-ink-muted">Your Trail</p>
                <p className="font-display text-xl text-ink">
                  {currentZone ? `You're on the ${currentZone.name}` : "At the Trailhead"}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" data-testid="your-trail-close" className="rounded-full p-2 text-ink-soft hover:text-ink">
                <X strokeWidth={1.5} className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-16">
              <Section icon={Map} title="Trail Profile" testId="section-profile">
                <TrailProfile currentPage={currentPage} zonesRead={state.zones_read || []} reducedMotion={settings.reduced_motion} onJump={(p) => { onJump(p); }} />
                {currentZone && <p className="mt-1 text-sm text-ink-soft">{currentZone.blurb}</p>}
              </Section>

              <Section icon={Waypoints} title="Reading the terrain" testId="section-checkin">
                <p className="mb-3 text-sm text-ink-soft">Where's your body on the trail today?</p>
                <div className="grid grid-cols-2 gap-2">
                  {checkinStates.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => chooseCheckin(c.id)}
                      data-testid={`checkin-${c.id}`}
                      className={`flex items-center gap-3 rounded-xl border p-2.5 text-left transition ${state.checkin === c.id ? "border-gold bg-gold/10" : "border-ivory-400/60 hover:border-gold/40"}`}
                    >
                      <span className="flex-none text-ink-soft"><ContourSwatch gap={c.contour} size={34} /></span>
                      <span className="font-display text-sm leading-tight text-ink">{c.label}</span>
                    </button>
                  ))}
                </div>
                {activeCheckin && (
                  <div className="mt-3 rounded-xl bg-ivory-200 p-3" data-testid="checkin-adaptation">
                    <p className="text-sm text-ink-soft">{activeCheckin.adaptation}</p>
                    <button type="button" onClick={() => reader.patchState({ checkin: null })} className="mt-1 text-xs text-gold-deep underline">Dismiss</button>
                  </div>
                )}
              </Section>

              <Section icon={NotebookPen} title="Field Journal" testId="section-journal">
                <p className="mb-2 text-xs text-ink-muted">Private to you. Tied to the {currentZone ? currentZone.name : "Trailhead"}.</p>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  placeholder="What did you notice in your body just now?"
                  data-testid="journal-input"
                  className="ruled w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={saveEntry} disabled={!draft.trim()} data-testid="journal-save" className="debossed-btn rounded-full px-4 py-2 text-sm text-ink disabled:opacity-40">Save entry</button>
                  <button type="button" onClick={() => reader.downloadJournalPdf()} disabled={!reader.journal.length} data-testid="journal-export" className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-ink-soft hover:text-gold-deep disabled:opacity-40"><Download className="h-4 w-4" />Download PDF</button>
                  {reader.journal.length > 0 && (
                    <button type="button" onClick={() => reader.deleteAllJournal()} data-testid="journal-delete-all" className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-terracotta-deep/80 hover:text-terracotta-deep"><Trash2 className="h-4 w-4" />Delete all</button>
                  )}
                </div>
                {flags?.bring_to_session && (
                  <div className="mt-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-soft" data-testid="journal-for-session">
                      <input type="checkbox" checked={forSession} onChange={(e) => setForSession(e.target.checked)} className="h-4 w-4 accent-[#A9833F]" />
                      Bring this to a session
                    </label>
                    {reader.journal.some((e) => e.for_session) && (
                      <button type="button" onClick={() => reader.downloadJournalPdf(true)} data-testid="journal-export-session" className="ml-3 inline-flex items-center gap-1.5 text-sm text-gold-deep underline-offset-4 hover:underline"><Download className="h-4 w-4" />Download for a counselor</button>
                    )}
                  </div>
                )}
                <div className="mt-3 space-y-2" data-testid="journal-list">
                  {reader.journal.slice().reverse().map((e) => (
                    <div key={e.id} className="rounded-lg bg-[#FCFBF7] p-3 ring-1 ring-ivory-400/50">
                      <p className="text-[11px] uppercase tracking-editorial text-gold-deep">{e.zone_name || "Field"} · {new Date(e.created_at).toLocaleDateString()}{e.for_session ? " · for a session" : ""}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{e.body}</p>
                    </div>
                  ))}
                  {!reader.journal.length && <p className="text-sm text-ink-muted">No entries yet — the notebook is waiting.</p>}
                </div>
              </Section>

              <Section icon={BookMarked} title="Field Notes" testId="section-notes">
                {notes.length ? (
                  <div className="flex flex-wrap gap-2">
                    {notes.map((n, i) => (
                      <span key={i} className="rounded-md bg-ivory-200 px-3 py-1.5 text-sm text-ink-soft">{n.title || n}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-ink-muted">Notes you open along the way are kept here, like a pocket notebook.</p>
                )}
              </Section>

              <Section icon={Footprints} title="Trail Log" testId="section-log">
                {log.length ? (
                  <ul className="space-y-2 border-l border-ivory-400/70 pl-4">
                    {log.slice(0, 12).map((l, i) => (
                      <li key={i} className="relative text-sm text-ink-soft">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-gold" />
                        {l.type === "zone" && `Entered ${l.name}`}
                        {l.type === "waypoint" && `Paused at ${l.name}`}
                        {l.type === "journal" && `Wrote a note${l.zone ? ` on the ${l.zone}` : ""}`}
                        {l.type === "checkin" && `Checked in: ${(checkinStates.find((c) => c.id === l.id) || {}).label || l.id}`}
                        <span className="ml-1 text-xs text-ink-muted">· {new Date(l.at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-ink-muted">Your journey will be drawn here as you go.</p>
                )}
              </Section>

              <Section icon={Settings2} title="Settings" testId="section-settings">
                <div className="space-y-4">
                  <div>
                    <p className="mb-1.5 text-xs text-ink-muted">Text size</p>
                    <div className="flex gap-2">{["sm", "base", "lg"].map((s) => <Chip key={s} active={settings.text_size === s} onClick={() => setSetting("text_size", s)}>{s === "sm" ? "Small" : s === "base" ? "Medium" : "Large"}</Chip>)}</div>
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs text-ink-muted">Theme</p>
                    <div className="flex gap-2">{[["warm", "Warm paper"], ["light", "Light"], ["dark", "Dark"]].map(([v, l]) => <Chip key={v} active={(settings.theme || "warm") === v} onClick={() => setSetting("theme", v)}>{l}</Chip>)}</div>
                  </div>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-ink-soft">Reduced motion</span>
                    <input type="checkbox" checked={!!settings.reduced_motion} onChange={(e) => setSetting("reduced_motion", e.target.checked)} data-testid="setting-reduced-motion" className="h-4 w-4 accent-[#A9833F]" />
                  </label>
                  {flags?.plain_words && (
                    <label className="flex items-center justify-between" data-testid="setting-plain-words-wrap">
                      <span className="text-sm text-ink-soft">Plain Words<span className="block text-xs text-ink-muted">A plain-language card under the densest pages.</span></span>
                      <input type="checkbox" checked={!!settings.plain_words} onChange={(e) => setSetting("plain_words", e.target.checked)} data-testid="setting-plain-words" className="h-4 w-4 accent-[#A9833F]" />
                    </label>
                  )}
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-ink-soft">Casey's Cadence on</span>
                    <input type="checkbox" checked={settings.cadence_on !== false} onChange={(e) => setSetting("cadence_on", e.target.checked)} data-testid="setting-cadence-on" className="h-4 w-4 accent-[#A9833F]" />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-ink-soft">Auto-start Cadence</span>
                    <input type="checkbox" checked={settings.cadence_autostart !== false} onChange={(e) => setSetting("cadence_autostart", e.target.checked)} className="h-4 w-4 accent-[#A9833F]" />
                  </label>
                  <div>
                    <p className="mb-1 text-xs text-ink-muted">Pacing</p>
                    <div className="flex flex-wrap gap-2">{["one page at a time", "a section", "read freely"].map((p) => <Chip key={p} active={(state.pacing || "read freely") === p} onClick={() => reader.patchState({ pacing: p })}>{p}</Chip>)}</div>
                  </div>
                </div>
              </Section>

              <Section icon={KeyRound} title="Save my place" testId="section-savecode">
                <p className="text-sm text-ink-soft">A trail code restores your progress, settings, Field Notes and journal on another device. Keep it private.</p>
                {codeShown && (
                  <div className="mt-3 rounded-xl bg-gold/10 p-3 text-center" data-testid="trail-code-display">
                    <p className="font-display text-2xl tracking-[0.2em] text-ink">{codeShown}</p>
                    <p className="mt-1 text-xs text-ink-muted">Copy this now — it's shown once.</p>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={async () => setCodeShown(await reader.regenerate())} data-testid="generate-code-btn" className="debossed-btn inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-ink"><RefreshCw className="h-4 w-4" />{reader.hasCode ? "New code" : "Generate code"}</button>
                  {reader.hasCode && <button type="button" onClick={() => { reader.revoke(); setCodeShown(null); }} data-testid="revoke-code-btn" className="rounded-full px-4 py-2 text-sm text-terracotta-deep/80 hover:text-terracotta-deep">Revoke code</button>}
                </div>
              </Section>

              <Section icon={ShieldCheck} title="Privacy" testId="section-privacy">
                <p className="text-sm leading-relaxed text-ink-soft">
                  No name, email, or password is ever asked. We save your reading place, settings, Field Notes and private journal against an anonymous ID on this device. Your journal is visible only to you and is never used for analytics. Your trail code lets you restore everything elsewhere; regenerate or revoke it anytime. Erasing removes everything and revokes your code.
                </p>
                <div className="mt-3">
                  {!confirmErase ? (
                    <button type="button" onClick={() => setConfirmErase(true)} data-testid="erase-btn" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-terracotta-deep/80 hover:text-terracotta-deep"><Trash2 className="h-4 w-4" />Erase everything</button>
                  ) : (
                    <div className="rounded-xl bg-terracotta-light/20 p-3" data-testid="erase-confirm">
                      <p className="text-sm text-ink-soft">This permanently erases your progress and journal. This can't be undone.</p>
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={async () => { await reader.erase(); setConfirmErase(false); onClose(); }} data-testid="erase-confirm-btn" className="rounded-full bg-terracotta-deep px-4 py-2 text-sm text-white">Yes, erase</button>
                        <button type="button" onClick={() => setConfirmErase(false)} className="rounded-full px-4 py-2 text-sm text-ink-soft">Keep my trail</button>
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
